import Autocomplete from "@/components/ui/molecules/autocomplete/Autocomplete";
import type { AutocompleteOption } from "@/components/ui/molecules/autocomplete/Autocomplete.type";
import { useGetSavedViewsList } from "@/hooks/useGetSavedViewsList";
import { useMemo } from "react";

interface SavedViewsDropdownProps {
  /**
   * Whether the field is required
   */
  isRequired?: boolean;
  /**
   * The feed ID to fetch saved views for
   */
  feedId?: string;
  /**
   * The selected saved view ID
   */
  value?: string | null;
  /**
   * Callback when the selected value changes
   */
  onValueChange?: (value: string | null, label?: string) => void;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Whether the dropdown is disabled
   */
  disabled?: boolean;
  /**
   * Size of the label
   */
  labelSize?: "sm" | "md" | "lg";
  /**
   * Custom className
   */
  className?: string;
}

/**
 * A dropdown component for selecting saved views to use in modals.
 * Fetches saved views for a specific feed and displays them in an autocomplete dropdown.
 */
export const SavedViewsDropdown = ({
  isRequired = false,
  feedId,
  value,
  onValueChange,
  placeholder = "Select a saved view",
  disabled = false,
  labelSize = "sm",
  className,
}: SavedViewsDropdownProps) => {
  const { items, isLoading } = useGetSavedViewsList({ feedId });

  // Convert saved views items to AutocompleteOption format
  const options: AutocompleteOption[] = useMemo(
    () =>
      items.map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [items],
  );

  return (
    <Autocomplete
      label={isRequired ? "Saved View*" : "Saved View"}
      labelSize={labelSize}
      options={options}
      value={value}
      onValueChange={(newValue, label) => {
        // Convert undefined to null for consistency
        onValueChange?.(newValue ?? null, label);
      }}
      placeholder={placeholder}
      disabled={disabled || !feedId || isLoading}
      emptyMessage="No saved views found for this feed"
      labelVariant="secondary"
      isPending={isLoading}
      focusText="Type to search or select a saved view"
      defaultToNone={false}
      className={className}
    />
  );
};
